export const CELESTIAL_BRAND_ID = 'FAF8FBF8-39E5-EC0E-0901-8AF684898EDC';
export const CELESTIAL_MARK_ID = 'FAEF55DC-EFEB-DF64-CAE4-7B84F98990E2';
export const CONCENTRATING_STATUS_ID = 'E7FDB796-26DA-E0F5-8F88-C21FE01D923B';
export function ft3dStatusVisualStyle(itemId: string): Ft3dStatusVisual['style'] | undefined {
  return itemId.toUpperCase() === CELESTIAL_BRAND_ID ? 'celestial-brand'
    : itemId.toUpperCase() === CELESTIAL_MARK_ID ? 'celestial-mark' : undefined;
}
export interface Ft3dStatusVisual {
  schema: 'ft.vfx.status.v1';
  style: 'celestial-brand' | 'celestial-mark';
  statusId: string;
  color: string;
  scale: number;
  targetHeight: number;
}
export interface Ft3dConcentrationAuraVisual {
  schema: 'ft.vfx.concentration-aura.v1';
  style: 'purchased-concentration-aura';
  statusId: string;
  originSpellId: string;
  texture: string;
  scale: number;
  targetHeight: number;
}
const uuid = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;
export function parseFt3dConcentrationAuraVisual(value: unknown, itemId: string): Ft3dConcentrationAuraVisual | undefined {
  if (itemId.toUpperCase() !== CONCENTRATING_STATUS_ID || !value || typeof value !== 'object') return;
  const v = value as Record<string, unknown>;
  if (v.schema !== 'ft.vfx.concentration-aura.v1' || v.style !== 'purchased-concentration-aura'
    || typeof v.statusId !== 'string' || !uuid.test(v.statusId)
    || typeof v.originSpellId !== 'string' || !uuid.test(v.originSpellId)
    || typeof v.texture !== 'string' || !/^Fab[A-Za-z0-9]+$/.test(v.texture)
    || typeof v.scale !== 'number' || !Number.isFinite(v.scale) || v.scale < .1 || v.scale > 4
    || typeof v.targetHeight !== 'number' || !Number.isFinite(v.targetHeight)
    || v.targetHeight < .1 || v.targetHeight > 1.2) return;
  return {schema:'ft.vfx.concentration-aura.v1',style:'purchased-concentration-aura',statusId:v.statusId,
    originSpellId:v.originSpellId,texture:v.texture,scale:v.scale,targetHeight:v.targetHeight};
}
/** Exact active caster concentration owns a published spell aura; recipient riders never do. */
export function compiledFt3dConcentrationAuraVisual(status: Record<string, any>, definition: Record<string, any> | null, targetId: string): Ft3dConcentrationAuraVisual | undefined {
  const scalar = (v: any): any => Array.isArray(v) ? v[0] : v;
  const statusItemId = String(scalar(status.status_item_id) ?? '').toUpperCase();
  const originSpellId = String(scalar(status.status_origin_id) ?? '').toUpperCase();
  const casterId = String(scalar(status.status_caster_id) ?? '').toUpperCase();
  const statusTargetId = String(scalar(status.status_target_id) ?? '').toUpperCase();
  const ownerId = targetId.toUpperCase();
  const statusId = String(scalar(status.status_id) ?? '');
  if (statusItemId !== CONCENTRATING_STATUS_ID || !uuid.test(originSpellId) || !uuid.test(statusId)
    || casterId !== ownerId || statusTargetId !== ownerId
    || Number(scalar(status.status_private) ?? 0) !== 0 || Number(scalar(status.status_deleted) ?? 0) !== 0
    || Number(scalar(status.status_paused) ?? 0) !== 0 || Number(scalar(status.status_campaign_paused) ?? 0) !== 0
    || Object.keys(status).some(k => k.startsWith('status_paused_campaign_') && Number(scalar(status[k]) ?? 0) !== 0)) return;
  const definitionId = String(scalar(definition?.items?.item_id ?? definition?.item_id) ?? '').toUpperCase();
  const recipe = definition?.assets?.magic_vfx;
  if (definitionId !== originSpellId || recipe?.schema !== 'ft.vfx.recipe.v1'
    || recipe.kind !== 'contact' || recipe.contactStyle !== 'purchased-concentration-aura'
    || recipe.lifecycle !== 'status' || recipe.anchor !== 'target' || recipe.blend !== 'alpha'
    || typeof recipe.texture !== 'string' || !/^Fab[A-Za-z0-9]+$/.test(recipe.texture)
    || typeof recipe.scale !== 'number' || !Number.isFinite(recipe.scale) || recipe.scale < .1 || recipe.scale > 4
    || typeof recipe.targetHeight !== 'number' || !Number.isFinite(recipe.targetHeight)
    || recipe.targetHeight < .1 || recipe.targetHeight > 1.2) return;
  return parseFt3dConcentrationAuraVisual({schema:'ft.vfx.concentration-aura.v1',style:'purchased-concentration-aura',
    statusId,originSpellId,texture:recipe.texture,scale:recipe.scale,targetHeight:recipe.targetHeight}, statusItemId);
}
export function parseFt3dStatusVisual(value: unknown, itemId: string): Ft3dStatusVisual | undefined {
  const style = ft3dStatusVisualStyle(itemId);
  if (!style || !value || typeof value !== 'object') return;
  const v = value as Record<string, unknown>;
  if (v.schema !== 'ft.vfx.status.v1' || v.style !== style
    || typeof v.statusId !== 'string' || !uuid.test(v.statusId)
    || typeof v.color !== 'string' || !/^#[0-9a-f]{6}$/i.test(v.color)
    || typeof v.scale !== 'number' || !Number.isFinite(v.scale) || v.scale < .1 || v.scale > .4
    || typeof v.targetHeight !== 'number' || !Number.isFinite(v.targetHeight) || v.targetHeight < .55 || v.targetHeight > .8) return;
  return {schema:'ft.vfx.status.v1',style,statusId:v.statusId,color:v.color,scale:v.scale,targetHeight:v.targetHeight};
}
/** Only an active, public, exact spell instance and its compiled published definition own this art. */
export function compiledFt3dStatusVisual(status: Record<string, any>, definition: Record<string, any> | null, targetId: string): Ft3dStatusVisual | undefined {
  const scalar = (v: any): any => Array.isArray(v) ? v[0] : v;
  const id = String(scalar(status.status_item_id ?? status.status_arr?.items?.item_id ?? status.item_id ?? status.items?.item_id) ?? '');
  const style = ft3dStatusVisualStyle(id);
  if (!style || String(scalar(status.status_target_id) ?? '').toUpperCase() !== targetId.toUpperCase()
    || Number(scalar(status.status_private) ?? 0) !== 0 || Number(scalar(status.status_deleted) ?? 0) !== 0
    || Number(scalar(status.status_paused) ?? 0) !== 0 || Number(scalar(status.status_campaign_paused) ?? 0) !== 0
    || Object.keys(status).some(k => k.startsWith('status_paused_campaign_') && Number(scalar(status[k]) ?? 0) !== 0)) return;
  const definitionId = String(scalar(definition?.items?.item_id ?? definition?.item_id) ?? '');
  const r = definition?.assets?.magic_vfx;
  if (definitionId.toUpperCase() !== id.toUpperCase() || r?.schema !== 'ft.vfx.recipe.v1' || r.kind !== 'contact'
    || r.lifecycle !== 'status' || r.contactStyle !== style || r.anchor !== 'target' || r.blend !== 'additive') return;
  return parseFt3dStatusVisual({schema:'ft.vfx.status.v1',style:r.contactStyle,statusId:scalar(status.status_id),color:r.color,scale:r.scale,targetHeight:r.targetHeight}, id);
}
